import { useMemo, useState } from 'react'
import { managerRollup, repLoad, areaConcentration, NO_OWNER } from '../../../lib/championships.js'
import { SINGLE_POINTS } from '../assignments.js'
import { Th } from './SortControls.jsx'
import styles from './pursuit.module.css'

/**
 * Coverage scorecard — "where are we exposed."
 *
 * SHARED VIEW: every category space renders it, so the totals are named with
 * the space's own noun rather than "championships". The default is the generic
 * noun for the same reason it is on the map — a caller that passes nothing gets
 * a word that is true everywhere, not the championship one that is true in
 * exactly one space.
 */
export default function ManagerRollup({
  items, now, nounPlural = 'opportunities', onSlice, onRep, onUnassigned,
}) {
  const r = useMemo(() => managerRollup(items, now), [items, now])
  const pct = Math.round(r.coverage * 100)
  const Plural = `${nounPlural.charAt(0).toUpperCase()}${nounPlural.slice(1)}`

  /* ⚠️ OVERSIGHT, MEASURED OFF THE LIVE RECORDS RATHER THAN ASSERTED.
   * `SINGLE_POINTS` says WHO oversees an area and at what concentration (read
   * at runtime from rep_proficiency.json, so a re-bundle updates it).
   * `areaConcentration` then measures, on the records in scope right now,
   * whether that area has actually been distributed. The map's figure is a
   * statement about won history; the live share is a statement about today, and
   * a manager needs the second one. Keyed by rep so a row can find its own. */
  const oversight = useMemo(() => {
    const byRep = new Map()
    for (const sp of SINGLE_POINTS) {
      if (!sp?.owner || !sp?.area) continue
      /* The area is matched on the record's own sport/segment text. Whole-token
       * matching, not substring: this repo has been bitten by `NC` inside
       * `NCHSAA` and by four distinct cheer gyms collapsing to one key. */
      const tokens = String(sp.area).toLowerCase().split(/[^a-z]+/).filter((t) => t.length > 3)
      if (!tokens.length) continue
      const match = (c) => {
        const hay = `${c?.sport || ''} ${c?.segment || ''} ${c?.division || ''}`
          .toLowerCase().split(/[^a-z]+/)
        return tokens.some((t) => hay.includes(t))
      }
      const conc = areaConcentration(items, match)
      if (!conc.inArea) continue
      byRep.set(sp.owner, { ...sp, conc })
    }
    return byRep
  }, [items])

  const money = (n) => '$' + Math.round(n).toLocaleString('en-US')

  // By-rep load, sortable on every column — rep name included, with the
  // Unassigned bucket always pinned last (it's a gap, not a person).
  const [sort, setSort] = useState({ field: 'total', dir: 'desc' })
  const reps = useMemo(() => {
    const mul = sort.dir === 'desc' ? -1 : 1
    return [...repLoad(items)].sort((a, b) => {
      if (a.rep === NO_OWNER && b.rep !== NO_OWNER) return 1
      if (b.rep === NO_OWNER && a.rep !== NO_OWNER) return -1
      if (sort.field === 'rep') return a.rep.toLowerCase() < b.rep.toLowerCase() ? -1 * mul : a.rep.toLowerCase() > b.rep.toLowerCase() ? 1 * mul : 0
      return ((a[sort.field] ?? 0) - (b[sort.field] ?? 0)) * mul
    })
  }, [items, sort])

  return (
    <div className={styles.rollup}>
      <div className={styles.scoreRow}>
        <Score big={`${pct}%`} label="Coverage" hint="assigned + dated ÷ upcoming" tone={pct >= 50 ? 'sage' : pct >= 20 ? 'amber' : 'crimson'} />
        <Score big={r.total.toLocaleString()} label={`Total ${nounPlural}`} hint="full universe loaded" />
        <Score big={r.upcoming.toLocaleString()} label="Upcoming (dated, future)" />
        <Score big={r.brokenDates.toLocaleString()} label="Broken / missing dates" tone="crimson" />
        {/* ⭐ THE NUMBER BRIAN COULD NOT TOUCH. A four-figure count of records
            with nobody on them, during championship season, rendered as inert
            text. It is now the door to the records themselves — where a rep can
            be picked and a reason written. `api.assignRep(id, rep, reason)`
            exists (src/lib/api.js:602) and the server REFUSES a change with no
            reason, so this is wired to a verified write path and not a hopeful
            one — the mistake made three times today. */}
        <Score
          big={r.noOwner.toLocaleString()}
          label="No rep assigned"
          tone="crimson"
          onOpen={onUnassigned}
          hint={onUnassigned
            ? 'open these and assign — a reason is required and goes in the ledger'
            : 'assigning is exec-only; your session can see this count but cannot write an assignment'}
        />
        <Score big={r.uncovered.toLocaleString()} label="Uncovered (≤ To Pursue)" tone="amber" />
      </div>

      <section className={styles.repSection}>
        <div className={styles.laneHead}>
          <span className={styles.laneTitle}>By representative</span>
          <span className={styles.laneCount}>{reps.length.toLocaleString()}</span>
          <span className={styles.laneHint}>
            click a column to sort · Unassigned is the expected state right now and always sorts last
          </span>
        </div>
        {/* Cards below 640px — the rep's name on its own line with the four
            counts wrapping under it, instead of a 505px table scrolled
            sideways in a 348px viewport. See src/styles/cc-mobile.css. */}
        <div className={`${styles.tableWrap} cc-scrollcue cc-cards-wrap`}>
          <table className={`${styles.table} cc-cards`}>
            <thead>
              <tr>
                <Th field="rep" label="Representative" sort={sort} setSort={setSort} />
                <Th field="total" label={Plural} sort={sort} setSort={setSort} />
                <Th field="toPursue" label="To pursue" sort={sort} setSort={setSort} />
                <Th field="won" label="Won" sort={sort} setSort={setSort} />
                <Th field="crowned" label="Crowned" sort={sort} setSort={setSort} />
                <Th field="value" label="Recorded value" sort={sort} setSort={setSort} />
              </tr>
            </thead>
            <tbody>
              {reps.map((row) => {
                const ov = oversight.get(row.rep)
                const isGap = row.rep === NO_OWNER
                return (
                  <tr key={row.rep}>
                    {/* ⚠️ THIS CELL IS THE FIX FOR BRIAN'S COMPLAINT, AND THE
                        ROW USED TO BE A BARE `<tr>` WITH NOTHING IN IT.
                        *"When I go into manager and see the reps I cannot do
                        anything wise like click on them."* It was literally
                        inert — and check-clickable passed it, because its rule
                        selected on a row-shaped CLASS NAME and this `<tr>`
                        carries none. Both the row and the gate are fixed; the
                        gate now fires on exactly this shape.

                        A button in the name cell rather than an onClick on the
                        `<tr>`: a table row is not a focusable control, so a
                        whole-row handler is unreachable by keyboard and
                        invisible to a screen reader. */}
                    <td className={isGap ? styles.tGap : styles.tName} data-col="name">
                      {isGap ? (
                        /* ⭐ THE UNASSIGNED BUCKET IS NOT A PERSON, SO IT GOES
                           SOMEWHERE ELSE: the records themselves, where they
                           can be assigned with a reason. This is the row Brian
                           could not touch during championship season. */
                        onUnassigned ? (
                          <button type="button" className={styles.repOpen} onClick={() => onUnassigned()}>
                            {row.rep} — open and assign →
                          </button>
                        ) : (
                          <span aria-disabled="true" title="Assigning is exec-only — your session cannot write an assignment.">
                            {row.rep}
                          </span>
                        )
                      ) : onRep ? (
                        <button type="button" className={styles.repOpen} onClick={() => onRep(row.rep)}>
                          {row.rep} — open what they are assigned →
                        </button>
                      ) : (
                        /* ⚠️ "BOOK" IS OWNERSHIP LANGUAGE AND THE GATE CAUGHT IT
                           HERE. check-ownership-language flagged two of these
                           labels: "nobody has a book — a rep holds an assignment
                           for a period." It is the right correction and not a
                           nicety, because the whole point of the assignment
                           engine is that an assignment is reversible and
                           auditable. A territory is not. */
                        <span aria-disabled="true" title="Opening a rep's assigned records is not available in this space.">
                          {row.rep}
                        </span>
                      )}
                      {/* ⚠️ OVERSIGHT IS NOT WORKLOAD, SAID ON THE ROW THAT
                          WOULD OTHERWISE MISREAD. Jennifer's cheer records are
                          oversight that she distributes; presenting them as
                          personal workload misstates the one figure a manager
                          acts on. And whether it HAS been distributed is
                          measured live, not assumed — Brian asked for that
                          directly. */}
                      {ov && (
                        <span className={styles.repOversight}>
                          {ov.area}: oversight, not personal workload — {ov.conc.topShare}% of the{' '}
                          {ov.conc.inArea.toLocaleString()} assigned {nounPlural} in this area sit with one person.
                          {ov.conc.distributedTo.length
                            ? ` Distributed to ${ov.conc.distributedTo.length} other${ov.conc.distributedTo.length === 1 ? '' : 's'}: `
                              + ov.conc.distributedTo.slice(0, 4).map((d) => `${d.rep} (${d.count.toLocaleString()})`).join(', ')
                              + '.'
                            : ' NOT DISTRIBUTED — nobody else holds any of it yet.'}
                          {ov.conc.unassigned
                            ? ` ${ov.conc.unassigned.toLocaleString()} in this area have no rep at all.`
                            : ''}
                        </span>
                      )}
                    </td>
                    <td className={styles.tNum} data-col="metricTotal" data-label={Plural}>{row.total.toLocaleString()}</td>
                    <td className={styles.tNum} data-col="metricToPursue" data-label="To pursue">{row.toPursue.toLocaleString()}</td>
                    <td className={styles.tNum} data-col="metricWon" data-label="Won">{row.won.toLocaleString()}</td>
                    {/* ⚠️ A BARE ZERO SAYS WHY IT IS ZERO. Chris Powell and Ilia
                        Jarostchuk hold no crowned records, and a lone `0` reads
                        as a broken screen rather than as the measured fact it
                        is. */}
                    <td className={styles.tNum} data-col="metricCrowned" data-label="Crowned">
                      {row.crowned
                        ? row.crowned.toLocaleString()
                        : <span className={styles.tZero} title={`None of ${row.rep === NO_OWNER ? 'these' : row.rep + "'s"} ${row.total.toLocaleString()} ${nounPlural} has a champion recorded yet. That is a measured state of the records, not a missing figure.`}>0 crowned</span>}
                    </td>
                    {/* ⚠️ VALUE WITH ITS DENOMINATOR, AND NEVER THE MODELLED
                        CEILING. Ranking by count is wrong by up to 65%, so the
                        money has to be here — but `value` is sparse, so the sum
                        travels with how many records carry one. The TAM chain's
                        ceiling is `headlineEligible: false` precisely so it
                        never appears beside a name, and it does not. */}
                    <td className={styles.tNum} data-col="metricValue" data-label="Recorded value">
                      {row.valueRecords
                        ? <>
                            {money(row.value)}
                            <span className={styles.tDenom}>
                              on {row.valueRecords.toLocaleString()} of {row.total.toLocaleString()}
                            </span>
                          </>
                        : <span className={styles.tZero} title="No amount is recorded on any of these records. That is a gap in the data, not an assignment worth nothing — do not rank on it.">no amount recorded</span>}
                    </td>
                  </tr>
                )
              })}
              {!reps.length && <tr><td colSpan={6} className={styles.emptyState}>No records in scope.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <div className={styles.rollupCols}>
        {/* ⚠️ A BREAKDOWN ROW IS A QUESTION: "which 412 are those?"
            It used to be a bar you could not press. Tapping now scopes the
            whole board to that slice, which is the only reason anybody reads
            a rollup. */}
        <Breakdown title="By level" field="level" rows={r.byLevel} total={r.total} onSlice={onSlice} />
        <Breakdown title="By sport" field="sport" rows={r.bySport.slice(0, 14)} total={r.total} onSlice={onSlice} />
      </div>
    </div>
  )
}

/**
 * A score card. `onOpen` makes it a real button; without it the card stays a
 * readout and is explicitly `aria-disabled`, so "this number does nothing" is
 * stated rather than merely true.
 */
function Score({ big, label, hint, tone, onOpen }) {
  if (onOpen) {
    return (
      <button type="button" className={`${styles.score} ${styles.scoreOpen}`} data-tone={tone || ''} onClick={onOpen}>
        <div className={styles.scoreBig}>{big}</div>
        <div className={styles.scoreLabel}>{label}</div>
        {hint && <div className={styles.scoreHint}>{hint}</div>}
      </button>
    )
  }
  return (
    <div className={styles.score} data-tone={tone || ''} aria-disabled={hint ? 'true' : undefined}>
      <div className={styles.scoreBig}>{big}</div>
      <div className={styles.scoreLabel}>{label}</div>
      {hint && <div className={styles.scoreHint}>{hint}</div>}
    </div>
  )
}

function Breakdown({ title, field, rows, total, onSlice }) {
  const max = Math.max(1, ...rows.map((r) => r.count))
  return (
    <div className={styles.breakdown}>
      <div className={styles.breakdownTitle}>{title}</div>
      {rows.map((row) => (
        <div
          key={row.k}
          className={styles.bdRow}
          data-clickable={onSlice ? '1' : '0'}
          /* ⚠️ `aria-disabled` IS NOT DECORATION HERE. Every handler on this row
             is `onSlice ? fn : undefined`, so when the parent omits the prop the
             row renders looking pressable and silently swallows the press — the
             shape check-clickable's new CONDITIONAL rule exists to catch, and
             the same shape as the dossier button and "Request an exec push".
             A role="button" that cannot be pressed has to say so to a screen
             reader anyway, so one attribute fixes the accessibility bug and
             satisfies the gate with the same truth. */
          aria-disabled={onSlice ? undefined : 'true'}
          role={onSlice ? 'button' : undefined}
          tabIndex={onSlice ? 0 : undefined}
          title={onSlice ? `Show only ${row.k}` : row.k}
          onClick={onSlice ? () => onSlice(field, row.k) : undefined}
          onKeyDown={onSlice ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSlice(field, row.k) } } : undefined}
        >
          <div className={styles.bdLabel} title={row.k}>{row.k}</div>
          <div className={styles.bdTrack}><div className={styles.bdFill} style={{ width: `${(row.count / max) * 100}%` }} /></div>
          <div className={styles.bdVal}>{row.count.toLocaleString()}<span className={styles.bdPct}>{Math.round((row.count / total) * 100)}%</span></div>
        </div>
      ))}
    </div>
  )
}
